import { Badge } from "@/components/ui/baseUi/badge"
import { withForm } from "@/components/ui/forms/hooks/useFormHook"
import { Text } from "@/components/ui/baseUi/text"
import { type CoachActivityTypeEnum } from "@/modules/coach/coachActivityTypes"
import { coachFormDataDefaultValues } from "@/modules/coach/types/coachFormTypes"
import { word } from "@/modules/core/utils/i18n"
import { useStore } from "@tanstack/react-form"
import { MailIcon, PhoneIcon } from "lucide-react"

export const CoachCreationReviewStep = withForm({
  defaultValues: coachFormDataDefaultValues,
  props: {
    selectedActivities: [] as CoachActivityTypeEnum[],
    otherActivities: [] as string[],
  },
  render: function CoachCreationReviewStepInner({ form, selectedActivities, otherActivities }) {
    const values = useStore(form.baseStore, (s) => s.values)

    const photoUrl = values.profilePhoto?.file
      ? URL.createObjectURL(values.profilePhoto.file)
      : (values.profilePhoto?.url ?? null)

    const location = [values.city, values.region, values.country].filter(Boolean).join(" . ")
    const allActivities = [
      ...selectedActivities.map((a) => word(`coach.activity.${a}`)),
      ...otherActivities.filter(Boolean),
    ]
    const formationChips = values.formation
      ? values.formation
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : []

    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col">
          <Text fontStyle="HL" className="font-bold text-textOnSurface-default">
            {word("coach.onboarding.review.title")}
          </Text>
          <Text fontStyle="BM" className="font-semibold text-textOnSurface-muted">
            {word("coach.onboarding.review.subtitle")}
          </Text>
        </div>

        <div className="flex flex-col gap-5 rounded-2xl bg-surface-background-secondary p-6">
          <ReviewSection label={word("coach.onboarding.review.section.profile")}>
            <div className="flex flex-col gap-1">
              {photoUrl ? (
                <img src={photoUrl} className="size-12 rounded-full object-cover" alt="" />
              ) : (
                <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-interactive-background-primary-faded">
                  <Text fontStyle="BM" className="font-semibold text-white">
                    {values.commercialName[0]?.toUpperCase()}
                  </Text>
                </div>
              )}
              <div className="flex min-w-0 flex-col">
                <Text fontStyle="BM" className="font-semibold text-textOnSurface-default">
                  {values.commercialName}
                  {values.role && <span className="font-normal text-textOnSurface-subtle"> . {values.role}</span>}
                </Text>
                {(allActivities.length > 0 || values.yearsOfExperience) && (
                  <Text fontStyle="BM" className="text-textOnSurface-subtle">
                    {[
                      allActivities.join(", "),
                      values.yearsOfExperience &&
                        word("coach.onboarding.review.years_experience", {
                          count: parseInt(values.yearsOfExperience, 10),
                        }),
                    ]
                      .filter(Boolean)
                      .join(" . ")}
                  </Text>
                )}
              </div>
            </div>
          </ReviewSection>

          {(values.email || values.phoneNumber.number) && (
            <ReviewSection label={word("coach.onboarding.review.section.contact")}>
              <div className="flex flex-col gap-1">
                {values.email && (
                  <div className="flex items-center gap-2">
                    <MailIcon className="size-4 shrink-0 text-textOnSurface-subtle" />
                    <Text fontStyle="BM" className="text-textOnSurface-default">
                      {values.email}
                    </Text>
                  </div>
                )}
                {values.phoneNumber.number && (
                  <div className="flex items-center gap-2">
                    <PhoneIcon className="size-4 shrink-0 text-textOnSurface-subtle" />
                    <Text fontStyle="BM" className="text-textOnSurface-default">
                      {values.phoneNumber.number}
                    </Text>
                  </div>
                )}
              </div>
            </ReviewSection>
          )}

          {location && (
            <ReviewSection label={word("coach.onboarding.review.section.location")}>
              <Text fontStyle="BM" className="text-textOnSurface-default">
                {location}
              </Text>
            </ReviewSection>
          )}

          {allActivities.length > 0 && (
            <ReviewSection label={word("coach.onboarding.review.section.activities")}>
              <div className="flex flex-wrap gap-2">
                {allActivities.map((label) => (
                  <Badge key={label} variant="primarySubtle" size={"lg"}>
                    <Text fontStyle="LM">{label}</Text>
                  </Badge>
                ))}
              </div>
            </ReviewSection>
          )}

          <ReviewSection label={word("coach.onboarding.review.section.photos")}>
            <Badge variant="neutralIntense" size={"lg"}>
              <Text fontStyle="LM">{word("coach.onboarding.review.section.photos_admin")}</Text>
            </Badge>
          </ReviewSection>

          {values.description && (
            <ReviewSection label={word("coach.onboarding.review.section.description")}>
              <Text fontStyle="BM" className="whitespace-pre-wrap text-textOnSurface-default">
                {values.description}
              </Text>
            </ReviewSection>
          )}

          {formationChips.length > 0 && (
            <ReviewSection label={word("coach.onboarding.review.section.formation")}>
              <div className="flex flex-wrap gap-2">
                {formationChips.map((chip) => (
                  <Badge key={chip} variant="primarySubtle" size={"lg"}>
                    <Text fontStyle="LM">{chip}</Text>
                  </Badge>
                ))}
              </div>
            </ReviewSection>
          )}
        </div>
      </div>
    )
  },
})

function ReviewSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <Text fontStyle="LM" className="font-semiboldtext-textOnSurface-subtle uppercase">
        {label}
      </Text>
      {children}
    </div>
  )
}
